import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { n8nService } from '../lib/n8nService';
import { calculateLaneProgress, cn } from '../lib/utils';
import { calculateStageMetrics, getProductDisplayName, getUrlsFromJsonb } from '../lib/helpers';
import { Layout } from '../components/Layout';
import { StageCard } from '../components/StageCard';
import { ProductCard, ProductCardSkeleton } from '../components/ProductCard';
import { SnipeModal } from '../components/SnipeModal';
import { ViewSwitch } from '../components/ViewSwitch';
import { Plus, Filter, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useNotifications } from '../lib/NotificationsContext';
import { PlatformBadge } from '../components/PlatformBadge';
import { useTranslation } from 'react-i18next';

const stages = ['sniped', 'researching', 'creative', 'winning', 'shopify'];

export default function Dashboard() {
  const [products, setProducts] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [winningDetails, setWinningDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showSnipeModal, setShowSnipeModal] = useState(false);
  const [view, setView] = useState('grid');
  const { addNotification, notifications } = useNotifications();
  const { t } = useTranslation();

  const fetchProducts = useCallback(async () => {
    try {
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (productsError) throw productsError;
      setProducts(productsData || []);

      // Fetch related data for each product
      if (productsData && productsData.length > 0) {
        const ecomIds = productsData.map(p => p.ecom_id);
        
        const [reviews, marketing, ugc, winning, winningImages, staticAds, shopify] = await Promise.all([
          supabase.from('product_reviews').select('*').in('ecom_id', ecomIds),
          supabase.from('product_marketing_angles').select('*').in('ecom_id', ecomIds),
          supabase.from('ugc_ads').select('*').in('ecom_id', ecomIds),
          supabase.from('winning_product_details').select('*').in('ecom_id', ecomIds),
          supabase.from('winning_product_images').select('*').in('ecom_id', ecomIds),
          supabase.from('static_ads').select('*').in('ecom_id', ecomIds),
          supabase.from('shopify').select('*').in('ecom_id', ecomIds)
        ]);

        setWinningDetails(winning.data || []);

        const dataMap = {};
        productsData.forEach(p => {
          dataMap[p.ecom_id] = {
            reviews: reviews.data?.find(r => r.ecom_id === p.ecom_id),
            marketing: marketing.data?.find(m => m.ecom_id === p.ecom_id),
            ugc: ugc.data?.find(u => u.ecom_id === p.ecom_id),
            winning: winning.data?.find(w => w.ecom_id === p.ecom_id),
            winningImages: winningImages.data?.find(wi => wi.ecom_id === p.ecom_id),
            staticAds: staticAds.data?.find(sa => sa.ecom_id === p.ecom_id),
            shopify: shopify.data?.find(s => s.ecom_id === p.ecom_id)
          };
        });
        setRelatedData(dataMap);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();

    // Set up real-time subscription
    const channel = supabase
      .channel('products-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, () => {
        fetchProducts();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchProducts]);

  // Calculate metrics for stage cards
  const stageMetrics = calculateStageMetrics(products, relatedData);

  const filteredProducts = products.filter(product => {
    if (activeFilter === 'all') return true;
    const data = relatedData[product.ecom_id] || {};
    
    // Filter based on stage
    switch (activeFilter) {
      case 'sniped':
        return !data.reviews && !data.marketing; // Only sniped, no research yet
      case 'researching':
        return (data.reviews || data.marketing) && !data.ugc && !data.staticAds;
      case 'creative':
        return (data.ugc || data.staticAds) && !data.winning;
      case 'winning':
        return data.winning && !data.shopify;
      case 'shopify':
        return !!data.shopify;
      default:
        return true;
    }
  });

  const handleSnipe = async ({ url, platform, language }) => {
    // Send to n8n webhook for processing
    // n8n will create the product record in the database and return a message
    const result = await n8nService.submitProduct(url, platform, language);

    // Derive a user-facing message from the webhook response
    let rawMessage = '';
    if (result) {
      if (typeof result === 'string') {
        rawMessage = result;
      } else if (result.rawText) {
        rawMessage = result.rawText;
      } else if (result.data && typeof result.data.message === 'string') {
        rawMessage = result.data.message;
      }
    }

    let notificationMessage = 'Product submitted successfully';
    if (rawMessage && rawMessage.toLowerCase().startsWith('#message:')) {
      notificationMessage = rawMessage.replace(/^#message:\s*/i, '').trim();
    }

    addNotification({
      type: 'submission',
      title: 'Product submission',
      message: notificationMessage,
      phase: 'submission',
    });

    toast.success('Product submitted for processing!');
    
    // Refresh products list after a short delay to allow n8n to process
    setTimeout(() => {
      fetchProducts();
    }, 2000);
  };

  return (
    <Layout>
      <div data-testid="dashboard">
        {/* Pipeline Stage Cards - Horizontal scroll on mobile */}
        <div className="bg-white border border-slate-200 rounded-xl py-4 md:py-6 px-4 md:px-6 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-[#1E1B4B] mb-4 md:mb-6 font-['Manrope']">
            {t('dashboard.pipelineOverview')}
          </h2>
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            <div className="flex md:grid md:grid-cols-5 gap-2 md:gap-4 min-w-max md:min-w-0">
              {stages.map(stage => (
                <div key={stage} className="w-28 md:w-auto flex-shrink-0 md:flex-shrink">
                  <StageCard
                    stage={stage}
                    metrics={stageMetrics[stage]}
                    isActive={activeFilter === stage}
                    onClick={() => setActiveFilter(activeFilter === stage ? 'all' : stage)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product Cards Grid */}
        <div>
          {/* Filter & View Switch */}
          <div className="flex items-center justify-between mb-4 md:mb-6 gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <Filter className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-600 truncate">
                {activeFilter === 'all'
                  ? t('dashboard.allProducts')
                  : `${activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}`}
              </span>
              <span className="px-2 py-0.5 bg-slate-200 rounded-full text-xs font-semibold text-slate-600 flex-shrink-0">
                {filteredProducts.length}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <ViewSwitch view={view} onViewChange={setView} />
            </div>
          </div>
          
          {/* Desktop Filter Tabs */}
          <div className="hidden md:flex gap-2 mb-6">
            <button
              onClick={() => setActiveFilter('all')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                activeFilter === 'all'
                  ? 'bg-cyan-500 text-white shadow-md'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-cyan-300'
              )}
              data-testid="filter-all"
            >
              All
            </button>
            {stages.map(stage => (
              <button
                key={stage}
                onClick={() => setActiveFilter(stage)}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize',
                  activeFilter === stage
                    ? 'bg-cyan-500 text-white shadow-md'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-cyan-300'
                  )}
                  data-testid={`filter-${stage}`}
                >
                  {stage}
                </button>
              ))}
            </div>

          {/* Mobile Filter Dropdown */}
          <div className="md:hidden mb-6">
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 text-slate-600 bg-white"
              data-testid="mobile-filter-select"
            >
              <option value="all">{t('dashboard.allProducts')}</option>
              {stages.map(stage => (
                <option key={stage} value={stage} className="capitalize">
                  {stage.charAt(0).toUpperCase() + stage.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Product Grid/List */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                <Filter className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">
                {t('dashboard.noProductsTitle')}
              </h3>
              <p className="text-slate-500 mb-6">
                {activeFilter === 'all' 
                  ? t('dashboard.noProductsAll')
                  : t('dashboard.noProductsStage', { stage: activeFilter })}
              </p>
              <button
                onClick={() => setShowSnipeModal(true)}
                className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-full shadow-lg shadow-cyan-500/30 transition-all"
                data-testid="empty-state-snipe-btn"
              >
                Snipe Your First Product
              </button>
            </div>
          ) : view === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {filteredProducts.map(product => {
                const data = relatedData[product.ecom_id] || {};
                const laneProgress = {
                  research: calculateLaneProgress(product, data, 'research'),
                  ugc: calculateLaneProgress(product, data, 'ugc'),
                  winning: calculateLaneProgress(product, data, 'winning'),
                  ads: calculateLaneProgress(product, data, 'ads'),
                  shopify: calculateLaneProgress(product, data, 'shopify')
                };
                const requiresApproval =
                  data.winning?.status === 'Pending User Approval';
                const hasPhaseNotification = notifications.some(
                  (n) =>
                    !n.read &&
                    n.type === 'phase_complete' &&
                    n.ecomId === product.ecom_id
                );
                return (
                  <ProductCard
                    key={product.ecom_id}
                    product={product}
                    laneProgress={laneProgress}
                    winningDetails={winningDetails}
                    compact={true}
                    requiresApproval={requiresApproval}
                    hasPhaseNotification={hasPhaseNotification}
                  />
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProducts.map(product => {
                const data = relatedData[product.ecom_id] || {};
                const displayName = getProductDisplayName(product.ecom_id, winningDetails);
                const images = getUrlsFromJsonb(product.image_urls);
                const heroImage = images[0];
                const laneProgress = {
                  research: calculateLaneProgress(product, data, 'research'),
                  ugc: calculateLaneProgress(product, data, 'ugc'),
                  winning: calculateLaneProgress(product, data, 'winning'),
                  ads: calculateLaneProgress(product, data, 'ads'),
                  shopify: calculateLaneProgress(product, data, 'shopify')
                };
                const totalProgress = Math.round(
                  (laneProgress.research + laneProgress.ugc + laneProgress.winning + laneProgress.ads + laneProgress.shopify) / 5
                );
                const hasPhaseNotification = notifications.some(
                  (n) =>
                    !n.read &&
                    n.type === 'phase_complete' &&
                    n.ecomId === product.ecom_id
                );
                
                return (
                  <a
                    key={product.ecom_id}
                    href={`/product/${product.ecom_id}`}
                    className={cn(
                      'bg-white rounded-xl border border-slate-200 overflow-hidden',
                      'hover:shadow-lg hover:border-cyan-300 transition-all block group'
                    )}
                    data-testid={`product-row-${product.ecom_id}`}
                  >
                    <div className="flex items-center gap-4 p-4">
                      {/* Image */}
                      <div className="w-14 h-14 md:w-16 md:h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {heroImage ? (
                          <img src={heroImage} alt={displayName} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Filter className="w-6 h-6 text-slate-300" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-[#1E1B4B] truncate text-sm md:text-base">
                            {displayName}
                          </h3>
                          {hasPhaseNotification && (
                            <span
                              className="ml-2 w-2 h-2 rounded-full bg-cyan-500"
                              title="New update available"
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <PlatformBadge platform={product.platform} size="sm" />
                          <span className="text-xs text-slate-400">•</span>
                          <span className="text-xs font-medium text-cyan-600">{totalProgress}% complete</span>
                        </div>
                      </div>

                      {/* Arrow */}
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-500 transition-colors flex-shrink-0" />
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* Floating Action Button */}
        <button
          onClick={() => setShowSnipeModal(true)}
          className={cn(
            'fixed bottom-8 right-8 z-50',
            'bg-cyan-500 hover:bg-cyan-600 active:scale-95',
            'text-white rounded-full p-5',
            'shadow-2xl hover:shadow-cyan-500/50',
            'transition-all duration-300 group'
          )}
          aria-label="Add new product"
          data-testid="fab-snipe-btn"
        >
          <Plus className="w-6 h-6" />
          <span className={cn(
            'absolute right-full mr-3 top-1/2 -translate-y-1/2',
            'bg-[#1E1B4B] text-white text-sm font-medium px-3 py-2 rounded-lg',
            'opacity-0 group-hover:opacity-100 transition-opacity',
            'whitespace-nowrap pointer-events-none'
          )}>
            Snipe Product
          </span>
        </button>

        {/* Snipe Modal */}
        <SnipeModal
          open={showSnipeModal}
          onOpenChange={setShowSnipeModal}
          onSnipe={handleSnipe}
        />
      </div>
    </Layout>
  );
}
